/**
 * The frame every authenticated page renders inside (PIVOT-124): a fixed
 * left sidebar carrying `AppNav` at >=1024px, the same `AppNav` as a top bar
 * below it — one component, two arrangements, decided in `AppNav.css` off
 * `.app-shell`'s row/column swap. Used as a route layout element, so the page
 * itself is the `<Outlet />` — see `router.tsx`.
 *
 * **`onboarding` (PIVOT-128).** `/onboarding` is a focused one-thing-at-a-time
 * flow (2026-08-07-plan-frontend-redesign.md §3d) — it should not also carry a
 * nav sidebar/top-bar fighting for attention. `router.tsx`'s two layout
 * wrappers (`OnboardingLayout` vs `ProtectedLayout`) already exist to tell
 * onboarding apart from every other authenticated route, so the signal is a
 * prop threaded from there rather than this component reading `useLocation()`
 * itself — that would couple `AppShell` to a route string it has no other
 * reason to know. `AppNav` is replaced, not simply removed (PIVOT-143): the
 * flow still needs the product's mark and a way to change language before
 * there is an account to navigate from, just not a sidebar fighting a
 * one-thing-at-a-time flow for attention. `.app-shell--onboarding` pins the
 * shell to a column past the 1024px row breakpoint below, so this always-
 * on-top header cannot end up beside `<main>` the way a sidebar would.
 *
 * `.page-content` is the shell-level ceiling (~1400px, plan §2.3's "data goes
 * wide"), not a per-page choice: pages narrow themselves by content type
 * below it — Profile caps its form at typing width, prose stays `ch`-based —
 * and this only stops data layouts stretching edge-to-edge on very wide
 * monitors.
 *
 * **Focus on navigation (PIVOT-134).** Routing swaps `<Outlet />`'s content
 * without ever moving focus or announcing anything, so a screen-reader user
 * who follows a sidebar link hears nothing and a keyboard user is left
 * wherever the previous page's DOM happened to leave the caret. The effect
 * below re-focuses `<main>` on every `pathname` change — `preventScroll`
 * because the browser's own navigation already handles scroll position, and
 * focusing without it would fight that. This reads `useLocation()` directly,
 * which the module docstring above says not to do for the nav-visibility
 * branch — a different concern: that one is about *which route*, this one
 * only needs *that the route changed*, so it stays decoupled from any
 * specific path string.
 */

import { useEffect, useRef } from 'react'
import { Outlet, useLocation } from 'react-router'
import { AppNav } from './AppNav'
import { LangSwitch } from './LangSwitch'
import { PivotLogo } from './PivotLogo'
import { ThemeToggle } from './ThemeToggle'
import { useI18n } from '../i18n'
import '../styles/header-actions.css'
import './AppShell.css'

interface AppShellProps {
  /** True only for `OnboardingLayout` — see the module docstring. */
  onboarding?: boolean
}

export function AppShell({ onboarding = false }: AppShellProps) {
  const { t } = useI18n()
  const { pathname } = useLocation()
  const mainRef = useRef<HTMLElement>(null)

  useEffect(() => {
    mainRef.current?.focus({ preventScroll: true })
  }, [pathname])

  return (
    <div className={onboarding ? 'app-shell app-shell--onboarding' : 'app-shell'}>
      {/* First focusable element on every authenticated page (WCAG 2.4.1):
          the sidebar's links plus the actions column both precede `<main>`
          in source order, so a keyboard user otherwise tabs through all of
          them before reaching the page itself on every single route. */}
      <a className="skip-link" href="#main">
        {t('a11y.skipToMain')}
      </a>
      {onboarding ? (
        // PIVOT-143: onboarding drops `AppNav` entirely (see the module
        // docstring), which also silently dropped the mark and the only way
        // to change language mid-flow. This is not AppNav's sidebar/top-bar
        // swap — a single top bar naming the product, at every width, with
        // nothing to navigate to but the two controls a candidate might
        // genuinely need before they have an account worth navigating from.
        <header className="app-shell-onboarding-header">
          <PivotLogo />
          <div className="header-actions">
            <LangSwitch />
            <ThemeToggle />
          </div>
        </header>
      ) : (
        <AppNav />
      )}
      <main id="main" className="app-main" tabIndex={-1} ref={mainRef}>
        <div className="page-content">
          <Outlet />
        </div>
      </main>
    </div>
  )
}
